// apps/fe/src/modules/catalog/services/api-services-authenticated.ts

import { EventBus } from '@/shared/utils';
import { CatalogEvents, CatalogEventKeys } from '../events';
import { CourseDataFilters } from '../types/filtered-data';
import { CategoryTypes } from '../types/catalog-category-data';
import { TaskEither } from '@shared-commons/utils';
import {
  buildLearningPathsFilter,
  buildMasterclassesFilter,
  buildSeriesFilter,
} from './filter-builder';
import { pendingRequests } from '../utils/cache-utils';

/**
 * Authenticated API services using automatic token refresh
 * @param fetchWithAuth Function from useAuthenticatedFetch hook
 * @param eventBus Optional event bus for emitting events
 */
export const createAuthenticatedCatalogServices = (
  fetchWithAuth: (url: string, options: RequestInit) => TaskEither<Error, Response>,
  eventBus?: EventBus<CatalogEvents>,
) => {
  /**
   * Helper function to handle API responses
   */
  const handleApiResponse = async <T>(
    responseTask: TaskEither<Error, Response>,
    errorEventKey?: keyof CatalogEvents,
  ): Promise<{ success: boolean; data?: T; error?: Error }> => {
    try {
      const result = await responseTask();

      if (result._tag === 'Left') {
        if (eventBus && errorEventKey) {
          eventBus.emit(errorEventKey, result.value);
        }
        return { success: false, error: result.value };
      }

      const response = result.value;

      if (!response.ok) {
        let errorMessage = `API call failed: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (parseError) {}

        const error = new Error(errorMessage);
        if (eventBus && errorEventKey) {
          eventBus.emit(errorEventKey, error);
        }
        return { success: false, error };
      }

      const data = await response.json();
      return { success: true, data };
    } catch (error: any) {
      console.error(`Error in API call:`, error);
      const errorObj = error instanceof Error ? error : new Error(String(error));

      if (eventBus && errorEventKey) {
        eventBus.emit(errorEventKey, errorObj);
      }

      return { success: false, error: errorObj };
    }
  };

  return {
    // Get user-specific catalog view data
    getUserCatalogView: async (email: string) => {
      // Create cache key for request deduplication
      const cacheKey = `catalog-view-${email}`;

      // Check if there's already a pending request for this data
      if (pendingRequests.has(cacheKey)) {
        return pendingRequests.get(cacheKey);
      }

      // Create new request
      const requestPromise = (async () => {
        const responseTask = fetchWithAuth(`${process.env.NEXT_PUBLIC_HOST}/api/catalog/view`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email }),
        });

        const result = await handleApiResponse(responseTask, CatalogEventKeys.catalog.error);

        // Remove from pending requests map once completed
        pendingRequests.delete(cacheKey);

        return result;
      })();

      // Store promise in pending requests map
      pendingRequests.set(cacheKey, requestPromise);

      return requestPromise;
    },

    // Get user-specific category data
    getUserCategoryData: async (email: string, categoryName: CategoryTypes) => {
      // Create cache key for request deduplication
      const cacheKey = `category-${email}-${categoryName}`;

      // Check if there's already a pending request for this data
      if (pendingRequests.has(cacheKey)) {
        return pendingRequests.get(cacheKey);
      }

      // Create new request
      const requestPromise = (async () => {
        const responseTask = fetchWithAuth(`${process.env.NEXT_PUBLIC_HOST}/api/catalog/category`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email, categoryName }),
        });

        const result = await handleApiResponse(responseTask, CatalogEventKeys.category.error);

        // Remove from pending requests map once completed
        pendingRequests.delete(cacheKey);

        return result;
      })();

      // Store promise in pending requests map
      pendingRequests.set(cacheKey, requestPromise);

      return requestPromise;
    },

    // User-specific search
    userSearch: async (email: string, query: string, courseTypes: string[] | undefined) => {
      // Create cache key for request deduplication
      const cacheKey = `search-${email}-${query}-${courseTypes?.join(',') || ''}`;

      // Check if there's already a pending request for this data
      if (pendingRequests.has(cacheKey)) {
        return pendingRequests.get(cacheKey);
      }

      // Create new request
      const requestPromise = (async () => {
        const responseTask = fetchWithAuth(`${process.env.NEXT_PUBLIC_HOST}/api/catalog/search`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email,
            query,
            courseType: courseTypes || ['Masterclass', 'Serie', 'Percorsi'],
          }),
        });

        const result = await handleApiResponse(responseTask, CatalogEventKeys.search.error);

        // Remove from pending requests map once completed
        pendingRequests.delete(cacheKey);

        return result;
      })();

      // Store promise in pending requests map
      pendingRequests.set(cacheKey, requestPromise);

      return requestPromise;
    },

    // User-specific filtered data
    getUserFilteredData: async (
      email: string,
      filters: Partial<CourseDataFilters>,
      page: { page: number; pageSize: number },
    ) => {
      // Create cache key based on the request parameters
      const cacheKey = `filtered-${email}-${JSON.stringify(filters)}-${page.page}`;

      // Check for an existing in-flight request
      if (pendingRequests.has(cacheKey)) {
        return pendingRequests.get(cacheKey);
      }

      // Create new request
      const requestPromise = (async () => {
        const responseTask = fetchWithAuth(`${process.env.NEXT_PUBLIC_HOST}/api/catalog/filtered`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email,
            page,
            seriesFilters: buildSeriesFilter(filters),
            learningPathFilter: buildLearningPathsFilter(filters),
            masterclassFilter: buildMasterclassesFilter(filters),
            courseType: filters.types || [],
          }),
        });

        const result = await handleApiResponse(responseTask, CatalogEventKeys.filter.error);

        // Remove from pending requests map once completed
        pendingRequests.delete(cacheKey);

        return result;
      })();

      // Store promise in pending requests map
      pendingRequests.set(cacheKey, requestPromise);

      return requestPromise;
    },

    // Create a favorite
    createFavourite: async (requestBody: Record<string, any>) => {
      const responseTask = fetchWithAuth(
        `${process.env.NEXT_PUBLIC_HOST}/api/catalog/favourites/create`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        },
      );

      return handleApiResponse(responseTask);
    },

    // Delete a favorite
    deleteFavourite: async (favouriteId: string, email: string) => {
      const baseUrl = `${process.env.NEXT_PUBLIC_HOST}/api/catalog/favourites/delete/${favouriteId}`;
      const url =
        typeof window !== 'undefined' ? new URL(baseUrl, window.location.origin) : new URL(baseUrl);

      url.searchParams.append('email', email);
      const responseTask = fetchWithAuth(url.toString(), {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      return handleApiResponse(responseTask);
    },

    // Get user-specific search filtered data
    getUserSearchFilteredData: async (
      email: string,
      filters: Partial<CourseDataFilters>,
      page: { page: number; pageSize: number },
      query: string,
    ) => {
      // Create cache key for request deduplication
      const cacheKey = `search-filtered-${email}-${query}-${JSON.stringify(filters)}-${page.page}`;

      // Check if there's already a pending request for this data
      if (pendingRequests.has(cacheKey)) {
        return pendingRequests.get(cacheKey);
      }

      // Create new request
      const requestPromise = (async () => {
        const responseTask = fetchWithAuth(`${process.env.NEXT_PUBLIC_HOST}/api/catalog/search`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email,
            page,
            seriesFilters: buildSeriesFilter(filters),
            learningPathFilter: buildLearningPathsFilter(filters),
            masterclassFilter: buildMasterclassesFilter(filters),
            courseType: filters.types || [],
            query,
          }),
        });

        const result = await handleApiResponse(responseTask, CatalogEventKeys.search.error);

        // Remove from pending requests map once completed
        pendingRequests.delete(cacheKey);

        return result;
      })();

      // Store promise in pending requests map
      pendingRequests.set(cacheKey, requestPromise);

      return requestPromise;
    },

    // Get user-specific category filtered data
    getUserCategoryFilteredData: async (
      email: string,
      filters: Partial<CourseDataFilters>,
      page: { page: number; pageSize: number },
      categoryName: CategoryTypes,
    ) => {
      // Create cache key based on the request parameters
      const cacheKey = `category-filtered-${email}-${categoryName}-${JSON.stringify(filters)}-${page.page}`;

      // Check for an existing in-flight request
      if (pendingRequests.has(cacheKey)) {
        return pendingRequests.get(cacheKey);
      }

      // Create new request
      const requestPromise = (async () => {
        const responseTask = fetchWithAuth(`${process.env.NEXT_PUBLIC_HOST}/api/catalog/category`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email,
            page,
            seriesFilters: buildSeriesFilter(filters),
            learningPathFilter: buildLearningPathsFilter(filters),
            masterclassFilter: buildMasterclassesFilter(filters),
            courseType: filters.types || [],
            categoryName: categoryName,
          }),
        });

        const result = await handleApiResponse(responseTask, CatalogEventKeys.category.error);

        // Remove from pending requests map once completed
        pendingRequests.delete(cacheKey);

        return result;
      })();

      // Store promise in pending requests map
      pendingRequests.set(cacheKey, requestPromise);

      return requestPromise;
    },
  };
};
